import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'

interface UseSurveyNavigationProps {
  assessmentId: string
  assessmentResponseId: string
  urlPage: number | undefined
  groupKeys: Array<string>
  questionGroups: Map<string, Array<{ id: string }>>
  answers: Record<string, number>
  savedProgress: { currentPage: number } | null
  questionsCount: number
  onPersistProgress: (page: number) => void
  /** Called on every page navigation with the current answers and target page
   * so they can be persisted server-side as a checkpoint. Fire-and-forget. */
  onSaveCheckpoint?: (answers: Record<string, number>, page: number) => void
}

export function getFurthestAccessiblePage(
  groupKeys: Array<string>,
  questionGroups: Map<string, Array<{ id: string }>>,
  answers: Record<string, number>,
): number {
  let furthestPage = 0

  for (let pageIndex = 0; pageIndex < groupKeys.length; pageIndex++) {
    const groupKey = groupKeys[pageIndex]
    const pageQuestions = questionGroups.get(groupKey) ?? []

    const allAnswered = pageQuestions.every((q) => q.id in answers)

    if (allAnswered) {
      furthestPage = pageIndex + 1
    } else {
      break
    }
  }

  return furthestPage
}

export function useSurveyNavigation({
  assessmentId,
  assessmentResponseId,
  urlPage,
  groupKeys,
  questionGroups,
  answers,
  savedProgress,
  questionsCount,
  onPersistProgress,
  onSaveCheckpoint,
}: UseSurveyNavigationProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const [currentPage, setCurrentPage] = useState(
    urlPage ?? savedProgress?.currentPage ?? 0,
  )

  const updatePageInUrl = useCallback(
    (newPage: number) => {
      if (
        !location.pathname.includes(
          `/assessments/${assessmentId}/responses/${assessmentResponseId}`,
        )
      ) {
        return
      }
      void navigate({
        to: '/assessments/$assessmentId/responses/$id',
        params: { assessmentId, id: assessmentResponseId },
        search: (prev) => ({ ...prev, page: newPage }),
        replace: true,
      })
    },
    [navigate, location.pathname, assessmentId, assessmentResponseId],
  )

  // Validate URL page when it changes.
  //
  // IMPORTANT: every state/URL write below is guarded by a value-changed check.
  // Unconditional writes here cause an infinite update loop because the router
  // notifies on every navigate (even to the same search), which re-runs this
  // effect, which navigates again, ad infinitum.
  useEffect(() => {
    if (urlPage === undefined) {
      return
    }

    let targetPage: number
    if (questionsCount === 0) {
      targetPage = savedProgress?.currentPage ?? 0
    } else {
      const furthestPage = getFurthestAccessiblePage(
        groupKeys,
        questionGroups,
        answers,
      )
      const maxAccessiblePage = furthestPage + 1

      if (urlPage > maxAccessiblePage) {
        targetPage = furthestPage
      } else if (urlPage < 0) {
        targetPage = 0
      } else {
        targetPage = urlPage
      }
    }

    if (targetPage !== currentPage) {
      setCurrentPage(targetPage)
    }
    if (targetPage !== urlPage) {
      updatePageInUrl(targetPage)
    }
  }, [
    urlPage,
    currentPage,
    questionsCount,
    savedProgress,
    groupKeys,
    questionGroups,
    answers,
    updatePageInUrl,
  ])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage < 0) return

      const furthestPage = getFurthestAccessiblePage(
        groupKeys,
        questionGroups,
        answers,
      )
      const maxAccessiblePage = furthestPage + 1
      const totalPages = groupKeys.length

      if (newPage > totalPages) return
      if (newPage > currentPage && newPage > maxAccessiblePage) return

      setCurrentPage(newPage)
      onPersistProgress(newPage)
      onSaveCheckpoint?.(answers, newPage)
      updatePageInUrl(newPage)

      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    },
    [
      groupKeys,
      questionGroups,
      answers,
      currentPage,
      onPersistProgress,
      onSaveCheckpoint,
      updatePageInUrl,
    ],
  )

  return { currentPage, handlePageChange }
}
